import React, { useState } from 'react';
import { Card } from '@components/atoms/Card';
import Alert from '@components/atoms/Alert';
import { SubscriptionTransaction, TransactionStatus, TransactionType } from '@models/subscription';
import { CheckCircle, XCircle, Clock, RefreshCw, CreditCard, DollarSign } from 'lucide-react';
import { useTranslation } from '@hooks/useTranslation';

interface BillingHistoryProps {
  transactions: SubscriptionTransaction[];
  isLoading?: boolean;
}

const BillingHistory: React.FC<BillingHistoryProps> = ({
  transactions,
  isLoading = false,
}) => {
  const [visibleCount, setVisibleCount] = useState(10);
  const tEnumLabels = useTranslation('enumLabels');
  const tBilling = useTranslation('billing');

  const getStatusConfig = (status: TransactionStatus) => {
    switch (status) {
      case TransactionStatus.SUCCESS:
        return {
          icon: CheckCircle,
          text: tEnumLabels.billingTransactionStatusSuccess,
          className: 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/20',
        };
      case TransactionStatus.FAILED:
        return {
          icon: XCircle,
          text: tEnumLabels.billingTransactionStatusFailed,
          className: 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/20',
        };
      case TransactionStatus.PENDING:
        return {
          icon: Clock,
          text: tEnumLabels.billingTransactionStatusPending,
          className: 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/20',
        };
      case TransactionStatus.REFUNDED:
        return {
          icon: RefreshCw,
          text: tEnumLabels.billingTransactionStatusRefunded,
          className: 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/20',
        };
      default:
        return {
          icon: Clock,
          text: status,
          className: 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700',
        };
    }
  };

  const getTypeConfig = (type: TransactionType) => {
    switch (type) {
      case TransactionType.CHARGE:
        return {
          icon: CreditCard,
          text: tEnumLabels.billingTransactionTypeCharge,
        };
      case TransactionType.REFUND:
        return {
          icon: RefreshCw,
          text: tEnumLabels.billingTransactionTypeRefund,
        };
      case TransactionType.RETRY:
        return {
          icon: RefreshCw,
          text: tEnumLabels.billingTransactionTypeRetry,
        };
      default:
        return {
          icon: DollarSign,
          text: type,
        };
    }
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const visibleTransactions = transactions.slice(0, visibleCount);
  const hasMore = visibleCount < transactions.length;

  if (isLoading) {
    return (
      <Card>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          {tBilling.history.title}
        </h2>
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">{tBilling.history.loading}</p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
        {tBilling.history.title}
      </h2>

      {transactions.length === 0 ? (
        <Alert variant="info" showIcon title={tBilling.history.noTransactionsTitle}>
          <p>{tBilling.history.noTransactionsBody}</p>
        </Alert>
      ) : (
        <div className="space-y-3">
          {visibleTransactions.map((transaction) => {
            const statusConfig = getStatusConfig(transaction.status);
            const typeConfig = getTypeConfig(transaction.type);
            const StatusIcon = statusConfig.icon;
            const TypeIcon = typeConfig.icon;

            return (
              <div
                key={transaction.transactionId}
                className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-400">
                    <TypeIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {typeConfig.text}
                      </p>
                      {transaction.nmiResponseText && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {transaction.nmiResponseText}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(transaction.createdAt)}
                    </p>
                    {transaction.nmiTransactionId && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                        {tBilling.history.transactionIdPrefix} {transaction.nmiTransactionId}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {formatAmount(transaction.amount)}
                  </p>
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${statusConfig.className}`}>
                    <StatusIcon className="w-3.5 h-3.5" />
                    <span className="text-xs font-medium">{statusConfig.text}</span>
                  </div>
                </div>
              </div>
            );
          })}

          {hasMore && (
            <div className="text-center pt-4">
              <button type="button"
                onClick={() => setVisibleCount((prev) => prev + 10)}
                className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium text-sm"
              >
                {tBilling.history.loadMoreButton}
              </button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default BillingHistory;
